import React, { useEffect, useState } from 'react'
import { message } from 'antd'
import { Box, Dialog, DialogContent, DialogTitle, Grid, Switch, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

import { ICategory } from '../../../types/category'
import { axiosInstance } from '../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'

interface ICategoriesModalProps {
  isOpen: boolean;
  onClose: (visible: boolean) => void;
  category?: ICategory;
  onUpdateCategorySuccess: () => void;
}

interface ICategoriesModal {
  name: string,
  description: string,
}

const getCategoryFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string().required('กรุณากรอกชื่อหมวดหมู่').min(3, 'ชื่อหมวดหมู่ต้องมีอย่างน้อย 3 ตัวอักษร'),
    description: Yup.string(),
  })
}

export const CategoryFormModal = (props: ICategoriesModalProps) => {
  const { isOpen, onClose, category, onUpdateCategorySuccess } = props

  const [isAvailable, setIsAvailable] = useState<boolean>(false)

  useEffect(() => {
    setIsAvailable(category?.isAvailable ?? false)
    formik.setValues(
      {
        name: category?.name || '',
        description: category?.description || '',
      },
    )
  }, [isOpen])

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
    },
    validationSchema: getCategoryFormValidationSchema(),
    onSubmit: async (values: ICategoriesModal) => {
      const isUpdate = !!category
      try {
        const reponse = await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate ? `/category/${category.objectId}` : '/category',
          data:
            {
              ...values,
              isAvailable,
            },
        })
        const { status } = reponse
        message.success(status === 201 ? 'สร้างหมวดหมู่สำเร็จ' : status === 200 ? `แก้ไขหมวดหมู่ ${category?.name} สำเร็จ` : 'บันทึกข้อมูลไม่สำเร็จ')
        setIsAvailable(false)
        formik.resetForm()
        onClose(false)
        onUpdateCategorySuccess()
      } catch (e: any) {
        const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
    },
  })

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="md">
      <DialogTitle>{category ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่'}</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="กรอกชื่อหมวดหมู่"
                name="name"
                helperText={
                  formik.touched.name && formik.errors.name as string
                }
                error={
                  !!(formik.touched.name && formik.errors.name)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="description"
                fullWidth
                multiline
                rows={4}
                label="กรอกรายละเอียด"
                name="description"
                helperText={
                  formik.touched.description && formik.errors.description as string
                }
                error={
                  !!(formik.touched.description && formik.errors.description)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.description}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <label>เปิดการใช้งาน</label>
              <Switch checked={isAvailable} onChange={() => setIsAvailable(!isAvailable)} />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                {category ? 'แก้ไขหมวดหมู่' : 'สร้างหมวดหมู่'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
